import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { Loader2, Search, Star, UserCheck, Users } from 'lucide-react';
import { Avatar, Badge, Button, Card, Spinner, TextArea } from '@tcrd/ui';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';
import { playChime } from '../../lib/sound';
import type { RideDto } from '../../lib/types';
import { useAuth } from '../../stores/auth';

const EVENT = 'tcrd:ride-referred';
export const announceReferral = (rideId: string) => window.dispatchEvent(new CustomEvent(EVENT, { detail: { rideId } }));

interface Candidate {
  id: string;
  name: string;
  photoUrl: string | null;
  rating: string;
  vehicle: string | null;
  available: boolean;
}

/** Conductor: referir una reserva a otro conductor de confianza (o ver/retirar la propuesta). */
export function ReferRide({ ride }: { ride: RideDto }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  // Búsqueda por nombre (espera a que deje de escribir).
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  const list = useQuery({
    queryKey: ['referable', ride.id, q],
    queryFn: () => api<Candidate[]>('/drivers/referable', { query: { rideId: ride.id, q: q || undefined } }),
    enabled: open,
    placeholderData: (prev) => prev,
  });
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['ride', ride.id] }), qc.invalidateQueries({ queryKey: ['rides'] })]);

  if (ride.status !== 'CONFIRMED') return null;
  if (ride.referral) {
    const d = ride.referral.driver;
    return (
      <Card className="p-4 border-2 border-amber-400/70 text-sm flex flex-col gap-2">
        <p>
          Referiste este viaje a <strong>{d.firstName} {d.lastName}</strong>. Esperando que el cliente acepte.
        </p>
        <Button
          size="sm"
          variant="ghost"
          onClick={() =>
            void api(`/rides/${ride.id}/refer`, { method: 'DELETE' })
              .then(refresh)
              .then(() => toast('Propuesta retirada'))
              .catch((e) => toast(errorMessage(e), 'danger'))
          }
        >
          Retirar la propuesta
        </Button>
      </Card>
    );
  }
  if (!open) {
    return (
      <Button variant="secondary" block icon={<Users className="size-5" />} onClick={() => setOpen(true)}>
        Referir a otro conductor
      </Button>
    );
  }

  const send = async () => {
    if (!chosen) return;
    setBusy(true);
    try {
      await api(`/rides/${ride.id}/refer`, { body: { driverId: chosen, note: note.trim() || undefined } });
      toast('Le avisamos al cliente para que acepte al nuevo conductor', 'success');
      setOpen(false);
      await refresh();
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-4 flex flex-col gap-3">
      <p className="font-semibold">¿A quién le pasas este viaje?</p>
      <p className="text-xs text-[var(--text-muted)]">El cliente recibe un aviso y decide si acepta al conductor que recomiendas o cancela. El precio no cambia.</p>
      <label className="flex items-center gap-2 h-12 rounded-2xl border border-[var(--border)] bg-[var(--surface)] px-3 focus-within:border-[var(--text)]">
        <Search className="size-5 text-[var(--text-muted)] shrink-0" aria-hidden />
        <span className="sr-only">Buscar conductor por nombre</span>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar conductor por nombre"
          className="flex-1 min-w-0 bg-transparent outline-none text-base"
          autoComplete="off"
        />
        {list.isFetching && <Loader2 className="size-4 animate-spin text-[var(--text-muted)]" aria-hidden />}
      </label>
      {list.isLoading && <Spinner />}
      {list.data?.length === 0 && (
        <p className="text-sm text-[var(--text-muted)]">{q ? `No hay conductores que coincidan con "${q}".` : 'No hay otros conductores activos en el negocio.'}</p>
      )}
      <div className="flex flex-col gap-2 max-h-80 overflow-y-auto" role="radiogroup" aria-label="Conductor">
        {list.data?.map((c) => (
          <button
            key={c.id}
            type="button"
            role="radio"
            aria-checked={chosen === c.id}
            disabled={!c.available}
            onClick={() => setChosen(c.id)}
            className={`tap flex items-center gap-3 rounded-2xl border p-3 text-left disabled:opacity-50 ${chosen === c.id ? 'border-brand-red bg-brand-red/5' : 'border-[var(--border)]'}`}
          >
            <Avatar src={c.photoUrl} firstName={c.name.split(' ')[0]} lastName={c.name.split(' ')[1]} size={44} />
            <span className="flex-1 min-w-0">
              <span className="block font-medium">{c.name}</span>
              <span className="block text-xs text-[var(--text-muted)] truncate">{c.vehicle ?? 'Sin vehículo activo'} · ★ {c.rating}</span>
            </span>
            {!c.available && <Badge tone="neutral">Ocupado</Badge>}
          </button>
        ))}
      </div>
      <TextArea label="Mensaje para el cliente (opcional)" placeholder="Ej.: Tengo una emergencia; Juan es de toda mi confianza." value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} />
      <div className="grid grid-cols-2 gap-2">
        <Button variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
        <Button variant="accent" loading={busy} disabled={!chosen} onClick={() => void send()}>Referir</Button>
      </div>
    </Card>
  );
}

/** Tarjeta del conductor referido con las dos opciones del cliente. */
export function ReferralDecision({ ride, onDone }: { ride: RideDto; onDone?: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState<'yes' | 'no' | null>(null);
  const r = ride.referral;
  if (!r || ride.status !== 'CONFIRMED') return null;

  const respond = async (accept: boolean) => {
    setBusy(accept ? 'yes' : 'no');
    try {
      await api(`/rides/${ride.id}/referral/respond`, { body: { accept } });
      toast(accept ? `Listo: ${r.driver.firstName} hará tu viaje` : 'Viaje cancelado sin cargo', accept ? 'success' : 'dark');
      await Promise.all([qc.invalidateQueries({ queryKey: ['ride', ride.id] }), qc.invalidateQueries({ queryKey: ['rides'] })]);
      onDone?.();
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <Avatar src={r.driver.photoUrl} firstName={r.driver.firstName} lastName={r.driver.lastName} size={56} />
        <div className="min-w-0">
          <p className="font-bold text-lg leading-tight" data-person>{r.driver.firstName} {r.driver.lastName}</p>
          <p className="text-sm text-[var(--text-muted)] flex items-center gap-1"><Star className="size-4 fill-current text-amber-500" aria-hidden /> {r.driver.rating}</p>
          {r.vehicle && <p className="text-sm text-[var(--text-muted)] truncate">{r.vehicle.make} {r.vehicle.model} · {r.vehicle.color} · <span className="font-mono">{r.vehicle.plate}</span></p>}
        </div>
      </div>
      {r.note && <p className="rounded-2xl bg-[var(--surface-2)] p-3 text-sm" data-no-translate>“{r.note}” — {ride.driver.firstName}</p>}
      <p className="text-sm text-[var(--text-muted)]">Viaje del {fmtDateTime(ride.scheduledAt)}. El precio acordado no cambia.</p>
      <div className="grid grid-cols-2 gap-2">
        <Button variant="secondary" loading={busy === 'no'} disabled={!!busy} onClick={() => void respond(false)}>Cancelar viaje</Button>
        <Button variant="accent" icon={<UserCheck className="size-5" />} loading={busy === 'yes'} disabled={!!busy} onClick={() => void respond(true)}>Aceptar</Button>
      </div>
    </div>
  );
}

/**
 * Cliente: aviso a pantalla completa "Has sido referido a otro conductor de confianza".
 * Aparece al llegar la notificación y, al abrir la app, si hay un referido pendiente.
 */
export function ReferralModal() {
  const history = useHistory();
  const isCustomer = useAuth((s) => !!s.user?.roles.includes('CUSTOMER'));
  const [rideId, setRideId] = useState<string | null>(null);
  const shown = useRef(new Set<string>());
  const upcoming = useQuery({ queryKey: ['rides', 'upcoming', 'referrals'], queryFn: () => api<RideDto[]>('/rides', { query: { scope: 'upcoming' } }), enabled: isCustomer, refetchOnWindowFocus: true });
  const ride = useQuery({ queryKey: ['ride', rideId], queryFn: () => api<RideDto>(`/rides/${rideId}`), enabled: !!rideId });

  useEffect(() => {
    const on = (e: Event) => {
      setRideId((e as CustomEvent<{ rideId: string }>).detail.rideId);
      playChime();
    };
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);
  useEffect(() => {
    const pending = upcoming.data?.find((r) => r.referral && r.status === 'CONFIRMED' && !shown.current.has(r.id));
    if (pending && !rideId) {
      shown.current.add(pending.id);
      setRideId(pending.id);
    }
  }, [upcoming.data, rideId]);

  const r = ride.data;
  if (!rideId || !isCustomer || (r && (!r.referral || r.status !== 'CONFIRMED'))) return null;
  return createPortal(
    <div role="alertdialog" aria-modal="true" aria-labelledby="referral-title" className="fixed inset-0 z-[3400] bg-black/60 flex items-end sm:items-center justify-center p-3">
      <div className="w-full max-w-md rounded-3xl bg-[var(--surface)] p-6 pb-[max(env(safe-area-inset-bottom),1.5rem)] flex flex-col gap-4">
        <div className="text-center">
          <Users className="size-10 mx-auto text-brand-red" aria-hidden />
          <h2 id="referral-title" className="mt-2 text-xl font-bold">Has sido referido a otro conductor de confianza</h2>
          {r && <p className="text-sm text-[var(--text-muted)] mt-1">{r.driver.firstName} no podrá atender tu viaje y te recomienda a:</p>}
        </div>
        {!r ? <Spinner /> : <ReferralDecision ride={r} onDone={() => setRideId(null)} />}
        <button
          className="tap text-sm underline text-[var(--text-muted)]"
          onClick={() => {
            const id = rideId;
            setRideId(null);
            history.push(`/app/rides/${id}`);
          }}
        >
          Decidir después
        </button>
      </div>
    </div>,
    document.body,
  );
}
